import { observable, action } from "mobx";
import { URLS, makeApi } from "Utils/apiURL";
import { toasterState } from "Components/common/toaster";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import userDetail from "Stores/userDetail";

class WhatsAppReplyState {
  /** Popup opened for Unipile auth — opener must close it; window.close() in popup often fails after cross-origin redirects. */
  waAuthPopup = null;

  @observable waConnected = false;
  @observable waAccounts = {};
  @observable waDataLoading = true;
  @observable showConfigure = false;
  @observable configureData = {};
  @observable connectLoading = false;
  @observable waAccountLimitReached = false;

  @action
  registerWaAuthPopup = (popup = null) => {
    this.waAuthPopup = popup;
  };

  @action
  clearWaAuthPopup = () => {
    this.waAuthPopup = null;
  };

  @action
  closeWaAuthPopupFromSuccess = () => {
    try {
      if (this.waAuthPopup && !this.waAuthPopup.closed) {
        this.waAuthPopup.close();
      }
    } catch (e) {
      // ignore
    }
    this.waAuthPopup = null;
  };

  @action
  setWaConnected = (value = false) => {
    this.waConnected = value;
  };

  @action
  setWaAccounts = (value = {}) => {
    this.waAccounts = value;
  };

  @action
  setWaDataLoading = (value = false) => {
    this.waDataLoading = value;
  };

  @action
  setShowConfigure = (value = false) => {
    this.showConfigure = value;
  };

  @action
  setConfigureData = (value = {}) => {
    this.configureData = value;
  };

  @action
  setConnectLoading = (value = false) => {
    this.connectLoading = value;
  };

  @action
  getCurrentTeam = async () => {
    try {
      const config = {
        url: URLS.getCurrentTeam,
        method: "GET"
      };
      const res = await makeApi(config);
      // Only merge fields present on the Team payload. Defaulting missing keys to 0 was wiping
      // correct noOfWaAccountsEligible from login/activeSubscription when the response shape differed
      // or a non-JSON error body was truthy.
      const ok = res?.status === 200 && res?.data != null && typeof res.data === "object";
      const payload = ok ? res.data : null;
      if (payload && !Array.isArray(payload)) {
        const patch = {};
        if (
          payload.noOfWaAccountsEligible !== undefined &&
          payload.noOfWaAccountsEligible !== null
        ) {
          patch.noOfWaAccountsEligible = Number(payload.noOfWaAccountsEligible);
        }
        if (
          payload.noOfWaAccountsUsed !== undefined &&
          payload.noOfWaAccountsUsed !== null
        ) {
          patch.noOfWaAccountsUsed = Number(payload.noOfWaAccountsUsed);
        }
        if (Object.keys(patch).length > 0) {
          userDetail.setUserFeatureFlag({
            ...userDetail.userFeatureFlag,
            ...patch
          });
          const ff = userDetail.userFeatureFlag;
          this.waAccountLimitReached =
            ff.noOfWaAccountsEligible > 0 &&
            ff.noOfWaAccountsUsed >= ff.noOfWaAccountsEligible;
        }
      }
    } catch (e) {
      console.error("WhatsApp getCurrentTeam error", e);
    }
  };

  @action
  getWhatsappAuthUrl = async () => {
    try {
      const config = {
        url: URLS.getWaAuthUrl,
        method: "GET"
      };
      const res = await makeApi(config);
      const { responseCode = "", url = "" } = res?.data || {};
      if (+responseCode === 200 && url) {
        if (url.includes("/wa/connect?target=")) {
          return url;
        }
        return `${window.location.origin}/wa/connect?target=${encodeURIComponent(
          url
        )}`;
      } else if (+responseCode === 409) {
        toasterState.setToastMsg(
          "A WhatsApp account is already connected.",
          "fail"
        );
      } else {
        toasterState.setToastMsg(
          "Failed to get WhatsApp auth URL. Please try again.",
          "fail"
        );
      }
    } catch (e) {
      console.error("WhatsApp auth URL error", e);
      toasterState.setToastMsg("Something went wrong. Please try again.", "fail");
    }
    return null;
  };

  @action
  confirmWhatsappConnection = async accountId => {
    try {
      this.setConnectLoading(true);
      const config = {
        url: URLS.connectWhatsAppAccount,
        method: "POST",
        data: { accountId }
      };
      const res = await makeApi(config);
      const { responseCode = "" } = res?.data || {};
      if (+responseCode === 200) {
        toasterState.setToastMsg(
          "WhatsApp account connected successfully!",
          "success"
        );
        const { noOfWaAccountsUsed = 0 } = userDetail?.userFeatureFlag || {};
        userDetail.setUserFeatureFlag({
          ...userDetail.userFeatureFlag,
          noOfWaAccountsUsed: noOfWaAccountsUsed + 1
        });
        this.setWaDataLoading(true);
        this.getWhatsappAccounts();
      } else if (+responseCode === 409) {
        toasterState.setToastMsg(
          "This WhatsApp account is already connected.",
          "fail"
        );
      } else {
        toasterState.setToastMsg(
          "Failed to connect WhatsApp account. Please try again.",
          "fail"
        );
      }
    } catch (e) {
      console.error("WhatsApp connection confirm error", e);
      toasterState.setToastMsg("Something went wrong. Please try again.", "fail");
    } finally {
      this.setConnectLoading(false);
    }
  };

  @action
  getWhatsappAccounts = async () => {
    try {
      const config = {
        url: URLS.getConnectedWhatsAppAccounts,
        method: "POST",
        data: {}
      };
      const res = await makeApi(config);
      this.setWaDataLoading(false);
      if (res?.data) {
        const { accounts } = res?.data || {};
        this.setWaConnected(accounts?.length > 0);
        this.setWaAccounts(res?.data);
      }
    } catch (e) {
      console.error("WhatsApp accounts get error", e);
      this.setWaDataLoading(false);
    }
  };

  @action
  deleteWhatsappAccount = async (accountId = "") => {
    if (!accountId) return;
    try {
      const config = {
        url: `${URLS.deleteWhatsAppAccount}/${accountId}`,
        method: "POST",
        data: {}
      };
      const res = await makeApi(config);
      confirmationPopupState.setShowPopup(false);
      const { responseCode = "" } = res?.data || {};
      if (+responseCode === 200) {
        toasterState.setToastMsg(
          "WhatsApp account deleted successfully",
          "success"
        );
        const { noOfWaAccountsUsed = 0 } = userDetail?.userFeatureFlag || {};
        userDetail.setUserFeatureFlag({
          ...userDetail.userFeatureFlag,
          noOfWaAccountsUsed: Math.max(0, noOfWaAccountsUsed - 1)
        });
        this.setWaDataLoading(true);
        this.getWhatsappAccounts();
      } else if (res?.status === 401) {
        toasterState.setToastMsg("Unauthorized access to delete", "fail");
      } else {
        toasterState.setToastMsg(
          "Something went wrong. Please try again later",
          "fail"
        );
      }
    } catch (e) {
      console.error("WhatsApp accounts delete error", e);
    }
  };
}

const whatsAppReplyState = new WhatsAppReplyState();

export { whatsAppReplyState };
